import { useCallback, useMemo, useRef, useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import type { Supply48DataDay } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { ListSupply48DataResponse } from '@ebalance/proto/bffuser/v1/supply_48_data_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  type GateCloseFrontier,
  gateCloseFrontierFromProto,
} from '~/models/gateClose'
import { useSaveSupply48Data } from '~/pages/Supply48Data/hooks/useSaveSupply48Data'
import {
  contractMwToThresholdKwh,
  isOverContractThreshold,
} from '~/pages/Supply48Data/models/contractCheck'
import type {
  CellChange,
  CellEdit,
  EditHistory,
} from '~/pages/Supply48Data/models/editHistory'
import {
  EMPTY_EDIT_HISTORY,
  canRedo,
  canUndo,
  pushHistory,
  redoHistory,
  undoHistory,
} from '~/pages/Supply48Data/models/editHistory'
import type {
  EditRowState,
  EditStateMap,
  UpdatedAtOverlay,
} from '~/pages/Supply48Data/models/editState'
import {
  getDirtyRows,
  isKomaManual,
  makeEditRowKey,
  recomputeRowState,
  revertRowToAuto,
  updateSlot,
} from '~/pages/Supply48Data/models/editState'
import { isCellLocked } from '~/pages/Supply48Data/models/gcLock'
import {
  buildDayIndex,
  underlyingSlotValue,
} from '~/pages/Supply48Data/models/gridRows'
import {
  buildSaveRows,
  editableSourceIds,
} from '~/pages/Supply48Data/models/saveRows'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'
import { isEditableSource } from '~/pages/Supply48Data/models/sourceKind'

/**
 * §10: editState + dayIndex + overlay + save/revert/discard 一式を担う hook。
 */
export type Supply48EditSessionState = {
  editState: EditStateMap
  dayIndex: ReturnType<typeof buildDayIndex>
  hasDirtyRows: boolean
  saveError: string | undefined
  isSaving: boolean
  /**
   * 複数セルをまとめて書き換える (AC-S4D-003 のキーボード操作: インライン編集 /
   * 貼付スピル / Ctrl+Enter 一括入力 / 範囲クリア / ↑↓ の増減)。
   * 1 回の呼び出し = undo 1 回で戻る 1 操作。
   *
   * coalesceKey が直前の呼び出しと一致する場合は履歴を新しく積まず畳み込む
   * (= 1 セルへの連続キーストロークを undo 1 回で入力前に戻す)。
   */
  applyCellEdits: (edits: CellEdit[], coalesceKey?: string) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
  /**
   * 契約値超過チェックを通してから保存する。 超過時は OverContractError を
   * throw して page 側の確認ダイアログに委ねる (AC-S4D-018)。 時間帯区分整合の
   * 警告 (AC-S4D-045) は doSave が返す InactiveKomaConfirmError がそのまま
   * ここを素通りして呼び出し元に伝わる (同じ page 側確認ダイアログに委ねる)。
   *
   * 戻り値は `doSave` と同じ「保存できたか」。
   */
  handleSave: () => Promise<boolean>
  /**
   * 保存を実行する。 **戻り値は「保存できたか」**。
   *
   * エラー表示自体は hook 内の `saveError` が担い、 呼び出し元へ throw しない
   * (確認ダイアログへ委ねる 2 種の警告だけが例外)。 そのため
   * **resolve したことを成功の判定に使えない** — 使うと保存に失敗した画面で
   * エラーバナーと成功 toast が同時に出る (devin review 指摘 #6759)。
   *
   * confirmInactiveKoma = true で AC-S4D-045 の警告を確認済みとして送信する。
   */
  doSave: (confirmInactiveKoma?: boolean) => Promise<boolean>
  doRevertLegToAuto: (legId: string) => void
  doRevertAllToAuto: () => void
  clearEditState: () => void
}

type Props = {
  data: ListSupply48DataResponse | undefined
  visibleDates: ProtoDate[]
  /**
   * 選択中の区分 (AC-S4D-001)。先物カテゴリは保有量確認のみで revert 対象外 —
   * doRevertLegToAuto / doRevertAllToAuto の多層防御ガードに使う (grid 側の
   * RevertButton 非表示 / index.tsx の manualEditCounts=0 を突破する直接呼び出し
   * 経路にも同じ制約を効かせる、issue #5129)。
   */
  category: SupplyCategory
  /**
   * 非定型の取引の列 (差金決済タブの列は除く、AC-S4D-060)。page が `nonStandardLegIds` で
   * 1 回だけ導出し、grid・未保存数の判定と同じ集合を渡す。
   */
  nonStandardLegIds: ReadonlySet<string>
}

/**
 * revert 後も手動変更フラグを維持するコマ番号 (= 値を据え置く GC ロック済みコマ)。
 * 据え置いたコマのフラグまで落とすと保存 payload の `manual_edit_komas` から消え、
 * 確定済みの手動変更が視覚区別を失う。
 */
function keptManualKomas(
  editRow: EditRowState | undefined,
  dayData: Supply48DataDay,
  date: ProtoDate,
  gateClose: GateCloseFrontier | undefined,
): number[] {
  const candidates = new Set<number>(dayData.manualEditKomas)
  for (const koma of editRow?.manualEditKomas ?? []) {
    candidates.add(koma)
  }
  return Array.from(candidates).filter(
    (koma) =>
      isCellLocked(gateClose, dayData, date, koma) &&
      isKomaManual(editRow, dayData.manualEditKomas, koma),
  )
}

export function useSupply48EditSession({
  data,
  visibleDates,
  category,
  nonStandardLegIds,
}: Props): Supply48EditSessionState {
  const { t } = useTranslation()
  const [editState, setEditState] = useState<EditStateMap>(new Map())
  const [saveError, setSaveError] = useState<string | undefined>(undefined)
  const [overlay, setOverlay] = useState<UpdatedAtOverlay>(new Map())
  const [history, setHistory] = useState<EditHistory>(EMPTY_EDIT_HISTORY)

  const { mutateAsync: saveAsync, isPending: isSaving } = useSaveSupply48Data()

  // §2: dayIndex を 1 回だけ useMemo 構築
  const dayIndex = useMemo(() => buildDayIndex(data?.sources ?? []), [data])

  /*
   * applyCellEdits / undo / redo は「現在の editState を読んで before を求める」
   * 必要があるが、editState を依存に含めるとキーストロークごとに関数 ID が変わり
   * グリッドの全セル (48 × leg 数) が再 render される。ref 経由で最新値を読み、
   * 関数 ID を安定させる (= GridCell の memo を効かせる)。
   */
  const editStateRef = useRef(editState)
  editStateRef.current = editState
  const dayIndexRef = useRef(dayIndex)
  dayIndexRef.current = dayIndex
  const historyRef = useRef(history)
  historyRef.current = history
  const dataRef = useRef(data)
  dataRef.current = data
  // 非定型の取引の列 (AC-S4D-060)。writeCells の関数 ID を安定させるため ref 経由で読む
  const nonStandardLegIdsRef = useRef(nonStandardLegIds)
  nonStandardLegIdsRef.current = nonStandardLegIds

  // data refetch (query key 変化) 時に overlay をクリア
  // dayIndex が変わる = data が更新された = overlay は不要
  const [prevDayIndex, setPrevDayIndex] = useState(dayIndex)
  if (prevDayIndex !== dayIndex) {
    setPrevDayIndex(dayIndex)
    setOverlay(new Map())
  }

  const hasDirtyRows = useMemo(
    () => getDirtyRows(editState).length > 0,
    [editState],
  )

  /**
   * セル書き込みを editState に反映し、before/after を CellChange として返す。
   * 履歴の積み方 (新規 / 畳み込み / 積まない) は呼び出し側が決める。
   */
  const writeCells = useCallback((edits: CellEdit[]): CellChange[] => {
    if (edits.length === 0) return []
    const nonStandardIds = nonStandardLegIdsRef.current
    const dIndex: DayIndex = dayIndexRef.current
    let next = editStateRef.current
    const changes: CellChange[] = []
    const touchedKeys = new Set<string>()

    for (const edit of edits) {
      const { legId, date, koma, value } = edit
      const key = makeEditRowKey(legId, date.year, date.month, date.day)
      const day = dIndex.get(key)
      // 履歴の before は保存対象化の判定 (recomputeRowState) と同じ「素の」値を
      // 使う (displaySlotValue の 0 fallback は表示専用の演出)。displaySlotValue
      // を使うと、サーバー値未設定の未編集セルへの undo (Ctrl+Z) が表示値 0 を
      // 編集値として書き戻し、recomputeRowState が保存値 null と不一致と判定して
      // dirty のまま保存対象化してしまう (Escape で直した #7689 と同じ経路。
      // self-review で発覚)
      const before = underlyingSlotValue(day, next.get(key)?.slots, koma - 1)
      const autoValue = day?.autoSlots[koma - 1]?.value
      // 非定型の取引の列は、自動計算値 (全コマ 0) と同じ値の入力でも手動変更の印を
      // 付ける (AC-S4D-060)。undo / redo / Escape は edit.mark で印を
      // 戻り先の状態へ戻す (undo / redo は履歴の印、Escape は focus 時点の印)。
      let markBefore: boolean | undefined
      let markOverride: boolean | undefined
      if (nonStandardIds.has(legId)) {
        const savedMarks = day?.manualEditKomas ?? []
        markBefore = isKomaManual(next.get(key), savedMarks, koma)
        markOverride = edit.mark ?? true
      }
      next = updateSlot(
        next,
        key,
        legId,
        date,
        koma,
        value,
        autoValue,
        markOverride,
      )
      touchedKeys.add(key)
      changes.push({ legId, date, koma, before, after: value, markBefore })
    }

    // 書き込み後に行フラグを保存済みの値基準で再計算する
    // (= undo / Escape で元の値へ戻したセルを dirty・手動編集のまま残さない)
    const recomputed = new Map(next)
    for (const key of touchedKeys) {
      const row = recomputed.get(key)
      if (row === undefined) continue
      const day = dIndex.get(key)
      recomputed.set(
        key,
        recomputeRowState(
          row,
          Array.from({ length: 48 }, (_, i) => day?.slots[i]?.value ?? null),
          Array.from({ length: 48 }, (_, i) => day?.autoSlots[i]?.value),
          nonStandardIds.has(row.legId)
            ? (day?.manualEditKomas ?? [])
            : undefined,
        ),
      )
    }
    next = recomputed

    // 書き込み後の印 (履歴の redo / Escape の復元先)。recompute で畳まれた行は保存済みの印に戻る
    for (const change of changes) {
      if (change.markBefore === undefined) continue
      const key = makeEditRowKey(
        change.legId,
        change.date.year,
        change.date.month,
        change.date.day,
      )
      change.markAfter = isKomaManual(
        next.get(key),
        dIndex.get(key)?.manualEditKomas ?? [],
        change.koma,
      )
    }

    // 同一イベント内で連続呼び出しされても before が古くならないよう ref も進める
    editStateRef.current = next
    setEditState(next)
    return changes
  }, [])

  const applyCellEdits = useCallback(
    (edits: CellEdit[], coalesceKey?: string) => {
      const changes = writeCells(edits)
      if (changes.length === 0) return
      const nextHistory = pushHistory(historyRef.current, changes, coalesceKey)
      historyRef.current = nextHistory
      setHistory(nextHistory)
    },
    [writeCells],
  )

  /**
   * 履歴の replay は GC ロックを再判定してから書き込む。
   * 編集中に refetch で GC 境界 (gate_close_frontier) が進むと、記録時点では未 GC だったコマが
   * 確定済みになりうる。素通しすると確定値を巻き戻したうえに backend が
   * FailedPrecondition を返し、同じ行の正当な編集ごと保存できなくなる。
   */
  const replayEdits = useCallback(
    (edits: CellEdit[]) => {
      const gateClose = gateCloseFrontierFromProto(
        dataRef.current?.gateCloseFrontier,
      )
      const dIndex = dayIndexRef.current
      const writable = edits.filter(
        (edit) =>
          !isCellLocked(
            gateClose,
            dIndex.get(
              makeEditRowKey(
                edit.legId,
                edit.date.year,
                edit.date.month,
                edit.date.day,
              ),
            ),
            edit.date as ProtoDate,
            edit.koma,
          ),
      )
      if (writable.length > 0) writeCells(writable)
    },
    [writeCells],
  )

  // undo / redo 自体は履歴を積まない (積むと戻れなくなる)
  const undo = useCallback(() => {
    const result = undoHistory(historyRef.current)
    if (result === null) return
    replayEdits(result.edits)
    historyRef.current = result.history
    setHistory(result.history)
  }, [replayEdits])

  const redo = useCallback(() => {
    const result = redoHistory(historyRef.current)
    if (result === null) return
    replayEdits(result.edits)
    historyRef.current = result.history
    setHistory(result.history)
  }, [replayEdits])

  const resetHistory = useCallback(() => {
    historyRef.current = EMPTY_EDIT_HISTORY
    setHistory(EMPTY_EDIT_HISTORY)
  }, [])

  /**
   * confirmInactiveKoma = true で時間帯区分整合の警告 (AC-S4D-045) を確認済みとして
   * 送信する。 false (既定) で違反があれば backend が FailedPrecondition を返し、
   * doSave はそれを InactiveKomaConfirmError として throw する (page 側の確認
   * ダイアログに委ねる、AC-S4D-018 の OverContractError と同じパターン)。
   */
  const doSave = useCallback(
    async (confirmInactiveKoma?: boolean): Promise<boolean> => {
      setSaveError(undefined)

      // AC-S4D-032: 市場 source は保存対象外。grid 側の read-only gate だけに
      // 依存せず、payload 構築の legIds からも除外する (editableSourceIds 参照)。
      const legIds = editableSourceIds(data?.sources ?? [])

      const { rows, hasStale } = buildSaveRows(
        editState,
        dayIndex,
        legIds,
        visibleDates,
        overlay,
      )

      if (hasStale) {
        setSaveError(t('page:supply48Data.serverError.staleEditState'))
        return false
      }

      try {
        const resp = await saveAsync({ rows, confirmInactiveKoma })
        // 成功後: 各行の新 updatedAt を overlay に格納 (§4)
        setOverlay((prev) => {
          const next = new Map(prev)
          for (const savedRow of resp.rows) {
            if (savedRow.deliveryDate === undefined) continue
            const k = makeEditRowKey(
              savedRow.supply48SourceId,
              savedRow.deliveryDate.year,
              savedRow.deliveryDate.month,
              savedRow.deliveryDate.day,
            )
            next.set(k, savedRow.updatedAt)
          }
          return next
        })
        // dirty フラグをリセット (editState の slots/manualEditKomas は維持し連続編集を可能にする)
        setEditState((prev) => {
          const next = new Map(prev)
          for (const [k, state] of next.entries()) {
            if (state.dirty) {
              next.set(k, { ...state, dirty: false })
            }
          }
          return next
        })
        // 保存で確定した以上、それ以前の操作は取り消せない (undo すると保存済みの値が
        // 巻き戻って未保存扱いに戻る)。design/supply/48data.html も保存確定で
        // undoStack / redoStack を捨てる。
        resetHistory()
        return true
      } catch (err) {
        if (err instanceof ConnectError) {
          if (err.code === Code.Aborted) {
            setSaveError(t('page:supply48Data.serverError.aborted'))
          } else if (err.code === Code.FailedPrecondition) {
            // AC-S4D-045: 印付きの FailedPrecondition は時間帯区分整合の警告。
            // page 側の確認ダイアログに委ねるため throw する (OverContractError と
            // 同じパターン)。印無しは GC 済みコマ拒否・受渡期間外の日への書込
            // 拒否で、どちらの理由にも当てはまる failedPrecondition 文言を出す。
            const inactiveKomaMessage = userFacingSaveMessage(err)
            if (inactiveKomaMessage !== null) {
              throw new InactiveKomaConfirmError(inactiveKomaMessage)
            }
            setSaveError(t('page:supply48Data.serverError.failedPrecondition'))
          } else if (err.code === Code.InvalidArgument) {
            // 印付きメッセージの producer は backend に 1 箇所 (AC-S4D-045) のみで
            // FailedPrecondition に固定されている (上の分岐)。InvalidArgument 側に
            // 印付きメッセージが飛んでくることはない (`supply_48_data_usecase:
            // build row` 等の内部診断文言や protovalidate の field path のみ) ので
            // userFacingSaveMessage は呼ばず常に汎用文言に倒す。
            setSaveError(t('page:supply48Data.serverError.unknown'))
          } else {
            setSaveError(t('page:supply48Data.serverError.unknown'))
          }
        } else {
          setSaveError(t('page:supply48Data.serverError.unknown'))
        }
        return false
      }
    },
    [
      editState,
      data,
      saveAsync,
      t,
      dayIndex,
      visibleDates,
      overlay,
      resetHistory,
    ],
  )

  const handleSave = useCallback(async (): Promise<boolean> => {
    const dirty = getDirtyRows(editState)
    // 変更が無い = 保存すべきものが無い。成功扱いにすると toast だけ出る。
    if (dirty.length === 0) return false

    setSaveError(undefined)

    // 契約値超過チェック (AC-S4D-018): 超えるセルを 1 セルずつ集める。
    // 確認ダイアログの件数 = 一覧の行数にするため、 判定と列挙は同じ走査で行う。
    const overCells: OverContractCell[] = []
    for (const [, rowState] of dirty) {
      const { legId: sourceId } = rowState
      const source = data?.sources.find((s) => s.supply48SourceId === sourceId)
      const contractVolumeMw =
        source?.detail.case === 'contract'
          ? source.detail.value.contractVolumeMw
          : undefined
      if (contractVolumeMw === undefined) continue
      const thresholdKwh = contractMwToThresholdKwh(
        Number(contractVolumeMw.value),
      )
      if (thresholdKwh === undefined) continue
      for (const [i, v] of rowState.slots.entries()) {
        if (!isOverContractThreshold(v, contractVolumeMw.value)) continue
        if (v === null || v === undefined) continue
        overCells.push({
          legId: sourceId,
          deliveryDate: rowState.deliveryDate,
          komaIdx: i,
          storedKwh: v,
          contractKwh: thresholdKwh,
        })
      }
    }

    if (overCells.length > 0) {
      // 契約値超過時は OverContractError を throw し、page 側の確認ダイアログ
      // (§6 pendingConfirm) に委ねる (ダイアログ統合は page レベルの責務)。
      throw new OverContractError(overCells)
    }

    return doSave()
  }, [editState, data, doSave])

  // 列単位 revert (§5 バグB修正: visibleDates × 非GCロックコマを走査)
  const doRevertLegToAuto = useCallback(
    (legId: string) => {
      if (data === undefined) return
      // AC-S4D-001 (issue #5129): 先物カテゴリ選択中は revert させない。
      // RevertButton は grid 側で isFuturesCategory を見て非表示にするが、
      // ボタンの見た目に依存しないガードをこの実行 seam にも重ねる (doRevertAllToAuto
      // と同じ多層防御方針)。
      if (category === SupplyCategory.FUTURES) return
      const source = data.sources.find((s) => s.supply48SourceId === legId)
      if (source === undefined) return
      // AC-S4D-032: 編集可能 source (allow-list、現状 CONTRACT のみ) 以外は
      // revert 対象にもしない。RevertButton は市場列に出さないため通常この経路
      // には来ないが、doRevertAllToAuto と同じ多層防御をこの唯一のガード無し
      // revert 経路にも揃える。
      if (!isEditableSource(source)) return
      const gateClose = gateCloseFrontierFromProto(data.gateCloseFrontier)
      setEditState((prev) => {
        let next = prev
        for (const date of visibleDates) {
          const key = makeEditRowKey(legId, date.year, date.month, date.day)
          const dayData = dayIndex.get(key)
          if (dayData === undefined) continue
          const autoSlots = dayData.autoSlots.map((s) => s.value ?? null)
          // GC ロック済みコマは据え置く
          const filteredAutoSlots = autoSlots.map((v, i) => {
            const komaNumber = i + 1
            if (isCellLocked(gateClose, dayData, date, komaNumber)) {
              // ロック済みコマはそのまま (元値を使う = revert から除外)
              const existing = prev.get(key)?.slots[i]
              if (existing !== undefined) return existing
              return dayData.slots[i]?.value ?? null
            }
            return v
          })
          next = revertRowToAuto(
            next,
            key,
            legId,
            { year: date.year, month: date.month, day: date.day },
            filteredAutoSlots,
            keptManualKomas(prev.get(key), dayData, date, gateClose),
          )
        }
        return next
      })
      // 列まるごとの置換は履歴モデル (セル単位の before/after) の外側の操作。
      // 古いスタックを残すと undo が revert 後の値を上書きして復元されるため捨てる
      // (revert 自体は確認ダイアログを挟む操作なので undo 対象から外す)。
      resetHistory()
    },
    [data, visibleDates, dayIndex, resetHistory, category],
  )

  // 全体 revert (§5 バグB修正: visibleDates × 非GCロックコマを走査)
  const doRevertAllToAuto = useCallback(() => {
    if (data === undefined) return
    // AC-S4D-001 (issue #5129): 先物カテゴリ選択中は revert させない
    // (doRevertLegToAuto と同じ理由・同じガード)。
    if (category === SupplyCategory.FUTURES) return
    const gateClose = gateCloseFrontierFromProto(data.gateCloseFrontier)
    setEditState((prev) => {
      let next = prev
      for (const source of data.sources) {
        // AC-S4D-032: 編集可能でない source (allow-list、現状 CONTRACT のみ) は
        // 保存対象外 (revert 対象にもしない)。市場行の auto_slots は現状常に空
        // という偶然に依存せず明示的に除外する (auto_slots が将来非空になっても
        // dirty 化 → 保存 payload 混入を防ぐ)。
        if (!isEditableSource(source)) continue
        for (const date of visibleDates) {
          const key = makeEditRowKey(
            source.supply48SourceId,
            date.year,
            date.month,
            date.day,
          )
          const dayData = dayIndex.get(key)
          if (dayData === undefined) continue
          const autoSlots = dayData.autoSlots.map((s) => s.value ?? null)
          // GC ロック済みコマは据え置く
          const filteredAutoSlots = autoSlots.map((v, i) => {
            const komaNumber = i + 1
            if (isCellLocked(gateClose, dayData, date, komaNumber)) {
              const existing = prev.get(key)?.slots[i]
              if (existing !== undefined) return existing
              return dayData.slots[i]?.value ?? null
            }
            return v
          })
          next = revertRowToAuto(
            next,
            key,
            source.supply48SourceId,
            { year: date.year, month: date.month, day: date.day },
            filteredAutoSlots,
            keptManualKomas(prev.get(key), dayData, date, gateClose),
          )
        }
      }
      return next
    })
    // doRevertLegToAuto と同じ理由で履歴を捨てる
    resetHistory()
  }, [data, visibleDates, dayIndex, resetHistory, category])

  const clearEditState = useCallback(() => {
    setEditState(new Map())
    setOverlay(new Map())
    resetHistory()
  }, [resetHistory])

  return {
    editState,
    dayIndex,
    hasDirtyRows,
    saveError,
    isSaving,
    applyCellEdits,
    undo,
    redo,
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    handleSave,
    doSave,
    doRevertLegToAuto,
    doRevertAllToAuto,
    clearEditState,
  }
}

/**
 * backend が「表示してよい spec 文言」に付ける印。
 * 正本は Go 側の `Supply48UserFacingErrorPrefix`
 * (go/internal/backend/application/usecase/supply/supply_48_time_zone_validation.go)。
 */
const USER_FACING_ERROR_PREFIX = '[S4D-045] '

/**
 * 保存エラーのうち、そのまま画面に出してよいサーバー文言を取り出す。
 *
 * 時間帯区分整合の警告 (AC-S4D-045) は「どのセルが契約の時間帯区分から外れて
 * いるか」を含む文言を backend が spec どおりに組み立てて返す (項目名・日付・
 * コマ・時間帯区分名は frontend が持っていない)。 印付きメッセージの producer は
 * backend に 1 箇所のみ (Supply48UserFacingErrorPrefix、code は
 * FailedPrecondition 固定)。同じ FailedPrecondition で GC 済みコマ拒否の汎用
 * 文言 (印無し) も流れてくるため、code だけで raw 表示に倒すと内部識別子が
 * 業務画面に出る。印が付いたものだけを表示対象にし、それ以外は null を返して
 * 汎用文言に倒す。`message` は connect-web が `[code] ` を前置するので
 * `rawMessage` を使う。
 */
function userFacingSaveMessage(err: ConnectError): string | null {
  if (!err.rawMessage.startsWith(USER_FACING_ERROR_PREFIX)) return null
  const body = err.rawMessage.slice(USER_FACING_ERROR_PREFIX.length)
  return body.length > 0 ? body : null
}

/**
 * §6 ConfirmDialog 統合用: 契約値超過検出時に throw するエラー。
 * page 側で catch して pendingConfirm を発火する。
 */
export class OverContractError extends Error {
  constructor(readonly cells: readonly OverContractCell[]) {
    super('over-contract')
    this.name = 'OverContractError'
  }
}

/** AC-S4D-018: 契約量を超えるセル 1 件 (値は保存単位 kWh/30min、komaIdx は 0 始まり)。 */
export type OverContractCell = {
  legId: string
  deliveryDate: { year: number; month: number; day: number }
  komaIdx: number
  storedKwh: number
  contractKwh: number
}

/**
 * §6 ConfirmDialog 統合用: AC-S4D-045 (時間帯区分整合) の警告検出時に throw する
 * エラー。 message は表示してよい spec 文言 (userFacingSaveMessage で印を除去
 * 済み)。 page 側で catch して pendingConfirm ('inactiveKoma') を発火し、
 * 確認後は confirmInactiveKoma=true で doSave を再実行する。
 */
export class InactiveKomaConfirmError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InactiveKomaConfirmError'
  }
}
